<template>
  <div class="min-h-screen bg-gray-50 pb-20">
    <Head :title="`Venta #${order.number_order ?? order.id}`" />
    
    <!-- ══ HEADER PREMIUM ══ -->
    <div class="bg-white backdrop-blur-md border-b border-orange-100 sticky top-0 z-30 px-6 py-4 mb-8 shadow-sm">
      <div class="max-w-[1600px] mx-auto flex items-center justify-between">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-2xl shadow-sm shadow-orange-200">
            <i class="fa fa-shopping-cart text-orange-500" />
          </div>
          <div>
            <h1 class="text-xl font-black text-gray-800 tracking-tight leading-none">Orden Confirmada</h1>
            <p class="text-[10px] font-bold text-orange-500 uppercase tracking-widest mt-1">Venta #{{ order.number_order }}</p>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <Link
            href="/pos" 
            class="h-11 px-6 bg-orange-500 text-white rounded-2xl hover:bg-orange-600 transition-all font-bold text-xs uppercase tracking-widest flex items-center gap-2 shadow-md shadow-orange-200 active:scale-95"
          >
            <i class="fas fa-plus" /> Nueva Venta
          </Link>
        </div>
      </div>
    </div>

    <!-- ══ CONTENIDO BENTO ══ -->
    <div class="max-w-[1600px] mx-auto px-6">
      <div class="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
        <!-- SECCIÓN IZQUIERDA: DETALLES Y PRODUCTOS (8/12) -->
        <div class="xl:col-span-8 flex flex-col gap-8">
          <!-- Card de Éxito / Hero -->
          <div class="bg-white rounded-[32px] p-10 border border-gray-100 shadow-sm relative overflow-hidden">
            <div class="absolute -right-20 -bottom-20 w-64 h-64 bg-green-50 rounded-full opacity-50 blur-3xl" />
            <div class="relative z-10 flex flex-col md:flex-row items-center gap-8">
              <div class="w-20 h-20 rounded-[24px] bg-green-500 text-white flex items-center justify-center text-3xl shadow-xl shadow-green-200">
                <i class="fas fa-check" />
              </div>
              <div class="text-center md:text-left flex-1">
                <h2 class="text-xl font-black text-gray-900 tracking-tight mb-2">¡Venta Procesada con Éxito!</h2>
                <div class="flex flex-wrap items-center gap-3 justify-center md:justify-start">
                  <span 
                    class="px-4 py-1.5 rounded-full font-black uppercase tracking-widest border"
                    :class="{
                      'bg-rose-50 text-rose-600 border-rose-100': order.order_state === 'cancel',
                      'bg-amber-50 text-amber-600 border-amber-100': order.order_state === 'request' || order.order_state === 'por_iniciar',
                      'bg-blue-50 text-blue-600 border-blue-100': order.order_state === 'pay' || order.order_state === 'en_preparacion',
                      'bg-purple-50 text-purple-600 border-purple-100': order.order_state === 'delivery' || order.order_state === 'pendiente_pago',
                      'bg-green-50 text-green-600 border-green-100': order.order_state === 'finish' || order.order_state === 'finalizado'
                    }"
                  >
                    {{ getOrderState(order.order_state) }}
                  </span>
                </div>
              </div>
              
              <div class="flex flex-wrap gap-2">
                <Link v-if="isRestaurantMode" href="/boards" class="h-12 px-6 rounded-2xl bg-orange-50 text-orange-600 hover:bg-orange-500 hover:text-white transition-all shadow-sm flex items-center justify-center gap-2 font-bold text-xs uppercase tracking-wider">
                  <i class="fas fa-columns" />
                  <span>Ver en Tablero</span>
                </Link>
                <Link v-else href="/pos" class="h-12 px-6 rounded-2xl bg-orange-50 text-orange-600 hover:bg-orange-500 hover:text-white transition-all shadow-sm flex items-center justify-center gap-2 font-bold text-xs uppercase tracking-wider">
                  <i class="fas fa-cash-register" />
                  <span>Nueva Venta</span>
                </Link>
                <button v-if="order.pays.length === 0 && !isEditing" class="h-12 px-8 rounded-2xl bg-blue-50 text-blue-500 hover:bg-blue-500 hover:text-white transition-all shadow-sm flex items-center justify-center" @click="isEditing = true">
                  <span class="text-xs font-bold uppercase tracking-widest">Editar venta</span>
                </button>
                <button v-if="isEditing" class="h-12 px-8 rounded-2xl bg-gray-50 text-gray-500 hover:bg-gray-200 transition-all shadow-sm flex items-center justify-center" @click="isEditing = false">
                  <span class="text-xs font-bold uppercase tracking-widest">Cancelar</span>
                </button>
                <button v-if="isEditing" :disabled="editForm.processing" class="h-12 px-8 rounded-2xl bg-green-500 text-white hover:bg-green-600 transition-all shadow-sm flex items-center justify-center disabled:opacity-50" @click="saveOrder">
                  <span class="text-xs font-bold uppercase tracking-widest">
                    <i v-if="editForm.processing" class="fas fa-spinner fa-spin mr-2" /> Guardar Cambios
                  </span>
                </button>
                <button v-if="(order.order_state === 'request' || order.order_state === 'por_iniciar') && !isEditing" class="h-12 px-8 rounded-2xl bg-rose-50 text-rose-500 hover:bg-rose-500 hover:text-white transition-all shadow-sm flex items-center justify-center" @click="cancelOrder">
                  <span class="text-xs font-bold uppercase tracking-widest">Anular venta</span>
                </button>
                <div class="flex flex-wrap items-center gap-2 rounded-2xl bg-gray-50 p-1.5 border border-gray-100">
                  <!-- Selector de Ancho: 58mm / 80mm -->
                  <div class="flex bg-white rounded-xl p-0.5 border border-gray-200 shadow-sm" title="Ancho de papel térmico">
                    <button 
                      type="button" 
                      :class="printSize === '58' ? 'bg-orange-500 text-white font-black shadow-sm' : 'text-gray-500 hover:text-gray-800 font-bold'"
                      class="px-2.5 py-1 text-[10px] rounded-lg transition-all"
                      @click="setPrintSize('58')"
                    >
                      58mm
                    </button>
                    <button 
                      type="button" 
                      :class="printSize === '80' ? 'bg-orange-500 text-white font-black shadow-sm' : 'text-gray-500 hover:text-gray-800 font-bold'"
                      class="px-2.5 py-1 text-[10px] rounded-lg transition-all"
                      @click="setPrintSize('80')"
                    >
                      80mm
                    </button>
                  </div>

                  <!-- Selector de Copias -->
                  <div class="flex items-center bg-white rounded-xl border border-gray-200 px-1.5 py-0.5 shadow-sm" title="Número de copias">
                    <button type="button" class="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-orange-600 font-black text-xs active:scale-90" @click="changePrintCopies(-1)">-</button>
                    <span class="text-[10px] font-black px-1.5 text-gray-800 select-none whitespace-nowrap">{{ printCopies }} {{ printCopies === 1 ? 'copia' : 'copias' }}</span>
                    <button type="button" class="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-orange-600 font-black text-xs active:scale-90" @click="changePrintCopies(1)">+</button>
                  </div>

                  <!-- Botones de Impresión USD / Bs. -->
                  <button type="button" class="px-3.5 py-2 bg-white rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-700 shadow-sm hover:text-orange-600 hover:border-orange-200 transition-all border border-gray-200 flex items-center gap-1.5 active:scale-95" @click="print('usd')">
                    <i class="fas fa-print opacity-60" /> USD
                  </button>
                  <button type="button" class="px-3.5 py-2 bg-white rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-700 shadow-sm hover:text-orange-600 hover:border-orange-200 transition-all border border-gray-200 flex items-center gap-1.5 active:scale-95" @click="print('ves')">
                    <i class="fas fa-print opacity-60" /> Bs.
                  </button>
                </div>
              </div>
            </div>

            <!-- ══ BARRA DE CAMBIO RÁPIDO DE ESTADO (VENDEDOR / MESERO - SOLO RESTAURANTES) ══ -->
            <div v-if="isRestaurantMode" class="mt-6 pt-5 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-3 relative z-10">
              <div class="flex items-center gap-2">
                <span class="w-8 h-8 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center text-xs flex-shrink-0">
                  <i class="fas fa-traffic-light" />
                </span>
                <div>
                  <span class="text-xs font-black text-gray-900 block leading-tight">Cambiar Estado del Pedido</span>
                  <span class="text-[11px] text-gray-400 font-medium">Actualiza al instante el flujo de la comanda</span>
                </div>
              </div>

              <div class="flex flex-wrap items-center gap-2">
                <!-- Segmented Control de Estados -->
                <div class="flex flex-wrap items-center gap-1 bg-gray-50 p-1 rounded-2xl border border-gray-200/80 shadow-xs">
                  <button 
                    v-for="s in restaurantStatesList" 
                    :key="s.key"
                    type="button"
                    :disabled="updatingStatus"
                    class="px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
                    :class="order.order_state === s.key ? s.activeClass : 'text-gray-500 hover:text-gray-900 hover:bg-white'"
                    @click="quickChangeState(s.key, s.key === 'finalizado')"
                  >
                    <span class="w-2 h-2 rounded-full" :class="order.order_state === s.key ? 'bg-white' : s.dotClass" />
                    <span>{{ s.label }}</span>
                  </button>
                </div>

                <!-- Botón de siguiente paso recomendado -->
                <button 
                  v-if="nextStateAction"
                  type="button"
                  :disabled="updatingStatus"
                  class="h-9 px-4 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 shadow-xs transition-all active:scale-95 text-white"
                  :class="nextStateAction.btnClass"
                  :title="`Avanzar comanda a ${nextStateAction.label}`"
                  @click="quickChangeState(nextStateAction.key, nextStateAction.markAsPaid)"
                >
                  <i class="fas" :class="[nextStateAction.icon, {'fa-spin': updatingStatus}]" />
                  <span>{{ nextStateAction.label }}</span>
                  <i class="fas fa-arrow-right text-[9px]" />
                </button>
              </div>
            </div>
          </div>

          <!-- Tabla de Productos Premium -->
          <div class="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden min-h-[400px]">
            <div class="p-8 border-b border-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h3 class="text-lg font-black text-gray-900 tracking-tight flex items-center gap-3 whitespace-nowrap">
                <i class="fas fa-list-ul text-orange-500" /> Resumen de Artículos
              </h3>
              <div v-if="isEditing" class="flex-1 w-full max-w-md relative">
                <div class="relative">
                  <i class="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input v-model="searchTerm" type="text" placeholder="Buscar producto para agregar..." class="w-full rounded-2xl bg-gray-50 border-none pl-10 pr-4 py-3 text-sm focus:ring-2 focus:ring-orange-500/20 transition-all shadow-inner" @input="searchProducts" />
                </div>
                <div v-if="searchTerm && searchResults.length > 0" class="absolute top-full left-0 right-0 bg-white border border-gray-100 rounded-xl shadow-xl mt-2 z-50 max-h-60 overflow-y-auto">
                  <button v-for="prod in searchResults" :key="prod.id" class="w-full text-left px-5 py-3 hover:bg-orange-50 border-b border-gray-50 last:border-0 flex justify-between items-center transition-colors" @click="addProduct(prod)">
                    <div class="flex-1 pr-4">
                      <span class="block text-sm font-bold text-gray-900 truncate">{{ prod.name }}</span>
                      <span class="block text-[10px] text-gray-400">Cod: {{ prod.code }} &bull; Disp: {{ prod.quantity }}</span>
                    </div>
                    <div class="text-right">
                      <span class="block text-sm font-black text-orange-500">${{ formatPrice(prod.prices && prod.prices.length ? prod.prices[0].price : 0) }}</span>
                    </div>
                  </button>
                </div>
              </div>
              <span class="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] whitespace-nowrap">{{ isEditing ? editForm.products.length : order.products.length }} Ítems</span>
            </div>
             
            <div class="overflow-x-auto pb-4">
              <table class="w-full">
                <thead>
                  <tr class="text-left">
                    <th class="px-8 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Producto</th>
                    <th class="px-8 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Cant.</th>
                    <th class="px-8 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Precio Un.</th>
                    <th class="px-8 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Subtotal</th>
                    <th v-if="isEditing" class="px-4 py-4" />
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-50">
                  <tr v-for="(product, key) in (isEditing ? editForm.products : order.products)" :key="key" class="hover:bg-gray-50/50 transition-colors">
                    <td class="px-8 py-5">
                      <div class="flex flex-col">
                        <span class="text-sm font-bold text-gray-900">{{ product.product.name }}</span>
                        <span class="text-[10px] font-medium text-gray-400">{{ product.product.description }}</span>
                      </div>
                    </td>
                    <td class="px-8 py-5 text-center">
                      <span v-if="!isEditing" class="px-3 py-1 rounded-lg bg-gray-100 text-gray-600 font-black text-xs">{{ product.quantity }}</span>
                      <input v-else v-model.number="product.quantity" type="number" min="1" class="w-16 text-center rounded border border-gray-200 p-1 text-sm font-bold bg-white focus:ring-orange-500 focus:border-orange-500" />
                    </td>
                    <td class="px-8 py-5 text-right">
                      <p class="text-sm font-black text-gray-900">${{ formatPrice(product.price) }}</p>
                      <p class="text-[10px] font-bold text-gray-400">Bs. {{ formatPrice(product.price * change) }}</p>
                    </td>
                    <td class="px-8 py-5 text-right">
                      <p class="text-sm font-black text-gray-900">${{ formatPrice(product.price * product.quantity) }}</p>
                      <p class="text-[10px] font-bold text-orange-500/50">Bs. {{ formatPrice((product.price * product.quantity) * change) }}</p>
                    </td>
                    <td v-if="isEditing" class="px-4 py-5 text-center">
                      <button class="text-red-500 hover:text-red-600" title="Eliminar producto" @click="editForm.products.splice(key, 1)"><i class="fas fa-trash" /></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
             
            <!-- Totales de la Tabla -->
            <div class="bg-gradient-to-br from-orange-50 to-amber-50 p-10 rounded-[32px] m-4 border border-orange-100">
              <div class="flex flex-col md:flex-row justify-between gap-8">
                <div class="space-y-4">
                  <div class="flex items-center justify-between w-64 text-orange-400">
                    <span class="text-xs font-bold uppercase tracking-widest">Total Bs.</span>
                    <span class="font-mono text-sm font-bold">{{ formatPrice(currentTotal * change) }}</span>
                  </div>
                  <div class="flex items-center justify-between w-64 text-red-400/80">
                    <span class="text-xs font-bold uppercase tracking-widest font-mono">Deuda USD</span>
                    <span class="text-sm font-bold">-${{ formatPrice(currentTotal - order.balance) }}</span>
                  </div>
                </div>
                  
                <div class="text-right">
                  <p class="text-[10px] font-black text-orange-500 uppercase tracking-[0.3em] mb-2 leading-none">Total</p>
                  <h2 class="text-5xl font-black tracking-tighter text-gray-800">
                    <span class="text-2xl font-medium text-orange-300 mr-1">$</span>{{ formatPrice(currentTotal) }}
                  </h2>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel de pagos (4/12) -->
        <div class="xl:col-span-4">
          <div v-if="['financial', 'admin', 'sales'].includes(role)" class="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm sticky top-28">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-black text-gray-900 flex items-center gap-3">
                <i class="fas fa-wallet text-orange-500" /> Gestión de Pagos
              </h2>
              <span v-if="pendingBalance <= 0" class="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center gap-1.5">
                <i class="fas fa-check-circle" /> Cuenta Saldada
              </span>
            </div>

            <!-- ══ WIDGET DIVIDIR CUENTA (SPLIT BILL) - SOLO RESTAURANTES ══ -->
            <div v-if="isRestaurantMode && pendingBalance > 0" class="mb-5 rounded-2xl border transition-all overflow-hidden" :class="isSplitActive ? 'bg-white border-orange-200/80 shadow-xs' : 'bg-gray-50/70 border-gray-200/70'">
              <!-- Barra de activación / Encabezado -->
              <div class="px-4 py-3 flex items-center justify-between">
                <div class="flex items-center gap-2.5">
                  <div class="w-7 h-7 rounded-lg flex items-center justify-center text-xs transition-all" :class="isSplitActive ? 'bg-orange-50 text-orange-600' : 'bg-white text-gray-500 border border-gray-200/80'">
                    <i class="fas fa-users text-[11px]" />
                  </div>
                  <div>
                    <div class="flex items-center gap-2">
                      <h3 class="text-xs font-semibold text-gray-800 leading-tight">Dividir cuenta</h3>
                      <span v-if="isSplitActive" class="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-600 border border-orange-200/50 whitespace-nowrap">
                        {{ splitPersons }} pers.
                      </span>
                    </div>
                    <p class="text-[10px] text-gray-400 font-normal leading-tight mt-0.5">
                      {{ isSplitActive ? 'Calculando cuotas compartidas' : 'Cobrar entre varios comensales' }}
                    </p>
                  </div>
                </div>

                <button 
                  type="button" 
                  class="h-7 px-2.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 active:scale-95 cursor-pointer"
                  :class="isSplitActive ? 'bg-gray-100 hover:bg-gray-200 text-gray-600' : 'bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-600 border border-gray-200/80 shadow-2xs'"
                  @click="toggleSplit"
                >
                  <i class="fas text-[9px]" :class="isSplitActive ? 'fa-times' : 'fa-plus'" />
                  <span>{{ isSplitActive ? 'Cerrar' : 'Dividir' }}</span>
                </button>
              </div>

              <!-- Panel expandido de División de Cuenta -->
              <div v-if="isSplitActive" class="px-4 pb-4 pt-2.5 border-t border-gray-100 space-y-3">
                <!-- Selector de comensales (stepper + chips en una sola fila) -->
                <div class="flex items-center justify-between gap-2">
                  <span class="text-[11px] font-medium text-gray-500 whitespace-nowrap">Comensales:</span>

                  <div class="flex items-center gap-1.5">
                    <!-- Stepper -->
                    <div class="flex items-center bg-gray-50 rounded-lg border border-gray-200/80 p-0.5">
                      <button 
                        type="button" 
                        class="w-6 h-6 rounded flex items-center justify-center font-bold text-gray-500 hover:bg-white hover:text-gray-800 text-xs transition-all active:scale-90" 
                        @click="setSplitPersons(splitPersons - 1)"
                      >
                        -
                      </button>
                      <span class="w-6 text-center text-xs font-semibold text-gray-800">{{ splitPersons }}</span>
                      <button 
                        type="button" 
                        class="w-6 h-6 rounded flex items-center justify-center font-bold text-gray-500 hover:bg-white hover:text-gray-800 text-xs transition-all active:scale-90" 
                        @click="setSplitPersons(splitPersons + 1)"
                      >
                        +
                      </button>
                    </div>

                    <!-- Chips Rápidos 2, 3, 4, 5, 6 -->
                    <div class="flex items-center gap-1">
                      <button 
                        v-for="n in [2, 3, 4, 5, 6]" 
                        :key="n"
                        type="button" 
                        class="h-7 px-2 rounded-lg text-[11px] font-medium transition-all"
                        :class="splitPersons === n ? 'bg-orange-500 text-white font-semibold shadow-2xs' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200/60'"
                        @click="setSplitPersons(n)"
                      >
                        {{ n }}p
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Tarjeta Principal de Cuota Calculada -->
                <div class="bg-gray-50/70 rounded-xl p-3 border border-gray-200/70 flex items-center justify-between gap-3">
                  <div>
                    <span class="text-[10px] font-medium text-gray-400 block leading-none">Cuota por persona</span>
                    <div class="flex items-baseline gap-1 mt-1">
                      <span class="text-xs font-medium text-orange-500">$</span>
                      <span class="text-lg font-bold text-gray-800 tracking-tight">{{ formatPrice(splitQuotaPerPerson) }}</span>
                      <span class="text-[10px] text-gray-400 font-normal">c/u</span>
                    </div>
                    <div class="text-[10px] text-gray-500 font-normal mt-0.5">
                      ≈ Bs. {{ formatPrice(splitQuotaPerPersonBs) }}
                    </div>
                  </div>

                  <div class="flex flex-col items-end">
                    <button 
                      type="button" 
                      class="h-7 px-3 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-lg shadow-2xs active:scale-95 transition-all flex items-center gap-1.5"
                      title="Asignar cuota a la caja de cobro"
                      @click="applySplitQuotaToPayment()"
                    >
                      <span>Cargar cuota</span>
                      <i class="fas fa-arrow-down text-[9px] opacity-80" />
                    </button>
                    <span class="text-[9px] text-gray-400 mt-1">Listo para cobrar</span>
                  </div>
                </div>

                <!-- Desglose de cada comensal (en filas limpias horizontales) -->
                <div class="space-y-1">
                  <div class="flex items-center justify-between text-[10px] font-medium text-gray-400 px-0.5">
                    <span>Detalle por comensal</span>
                    <span>Clic para cargar</span>
                  </div>
                  <div class="space-y-1 max-h-36 overflow-y-auto pr-0.5">
                    <div 
                      v-for="payer in splitPayerList" 
                      :key="payer.index"
                      class="px-2.5 py-1.5 rounded-lg border border-gray-100 bg-white hover:bg-orange-50/60 hover:border-orange-200/70 transition-all cursor-pointer flex items-center justify-between group"
                      @click="applySplitQuotaToPayment(payer.amount)"
                    >
                      <span class="text-[11px] font-medium text-gray-700 group-hover:text-orange-600 flex items-center gap-1.5">
                        <i class="fas fa-user text-[9px] text-gray-300 group-hover:text-orange-400" />
                        {{ payer.label }}
                      </span>
                      <div class="flex items-center gap-2">
                        <span class="text-[10px] text-gray-400 font-normal">Bs. {{ formatPrice(payer.amountBs) }}</span>
                        <span class="text-xs font-semibold text-gray-800 group-hover:text-orange-600">${{ formatPrice(payer.amount) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Mini progreso si hay pagos -->
                  <div v-if="order.pays && order.pays.length > 0" class="pt-1 flex items-center justify-between text-[10px] text-gray-400 px-0.5">
                    <span>Pagado: ${{ formatPrice(order.balance) }}</span>
                    <span class="text-gray-600 font-medium">Resta: ${{ formatPrice(pendingBalance) }}</span>
                  </div>
                </div>

                <!-- Botón Imprimir Ticket Comensales Dividido -->
                <div class="pt-0.5">
                  <button 
                    type="button" 
                    class="w-full h-8 px-3 rounded-lg border border-gray-200/80 bg-white hover:bg-gray-50 hover:border-orange-200 text-gray-600 hover:text-orange-600 text-xs font-medium transition-all flex items-center justify-center gap-1.5 active:scale-98 cursor-pointer" 
                    title="Imprimir ticket térmico con el desglose para la mesa"
                    @click="printSplitBill"
                  >
                    <i class="fas fa-print text-gray-400 text-xs" />
                    <span>Imprimir ticket comensales ({{ splitPersons }}p)</span>
                  </button>
                </div>
              </div>
            </div>

            <payment-box 
              :quick="true"
              :payment_methods="payment_methods"
              :balance="pendingBalance"
              :split-amount="selectedSplitQuota"
              :change="change"
              :order="order"
            />

            <!-- Historial de pagos -->
            <div class="mt-10">
              <h3 class="text-xs font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Historial de Transacciones</h3>
              
              <div class="space-y-4">
                <div 
                  v-for="pay in order.pays" 
                  :key="pay.id"
                  class="bg-gray-50 p-6 rounded-2xl border border-gray-100 hover:bg-orange-50 transition-colors group relative"
                >
                  <button 
                    v-if="['financial', 'admin'].includes(role)" 
                    class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-red-100 text-red-500 hover:bg-red-500 hover:text-white transition-colors opacity-0 group-hover:opacity-100" 
                    title="Eliminar Pago"
                    @click="deletePay(pay.id)"
                  >
                    <i class="fas fa-trash text-xs" />
                  </button>
                  <div class="flex justify-between items-start pr-6">
                    <div class="flex flex-col">
                      <span class="capitalize font-black text-gray-900 text-sm mb-1">{{ pay.method }}</span>
                      <span class="text-[10px] font-medium text-gray-400">{{ pay.created_at }}</span>
                    </div>
                    <div class="text-right">
                      <p class="font-black text-gray-900 text-sm tracking-tighter">${{ formatPrice(pay.amount) }}</p>
                      <p v-if="pay.amount_bs > 0" class="text-[10px] font-bold text-orange-500">
                        Bs. {{ formatPrice(pay.amount_bs) }}
                      </p>
                    </div>
                  </div>
                  <div v-if="pay.reference" class="mt-3 pt-3 border-t border-gray-100/50 flex items-center gap-2">
                    <i class="fas fa-receipt text-[10px] text-gray-300" />
                    <span class="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Ref: {{ pay.reference }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LayoutFullVue from '../../Shared/LayoutFull.vue'
import TextInput from '../../Shared/TextInput.vue'
import { Head, Link } from '@inertiajs/inertia-vue3'
import PaymentBox from '../../Shared/PaymentBox.vue'

export default {
  
  components: {
    TextInput,
    Head,
    PaymentBox,
    Link,
  },
  layout: LayoutFullVue,

  props: {
    order: {
      type: Object,
      required: true,
    },
    role: {
      type: String,
      required: true,
    },
    payment_methods: {
      type: Object,
      required: true,  
    },
    change: {
      type: Number,
      required: true,
    },
    products: {
      type: Object,
      default: () => ({ data: [] }),
    },
  },

  data() {
    let discountPercentage = this.order.subtotal > 0 ? (this.order.discount * 100 / this.order.subtotal) : 0
    return {
      searchTerm: '',
      searchTimeout: null,
      isEditing: false,
      editForm: this.$inertia.form({
        client: { id: this.order.client_id || (this.order.client ? this.order.client.id : '') },
        date: this.order.date,
        products: this.order.products.map(p => ({
          product_id: p.product_id,
          quantity: p.quantity,
          price: p.price,
          product: p.product,
        })),
        discount: {
          type: 'percentage',
          value: discountPercentage,
        },
        comentary: this.order.comentary || '',
      }),
      form: this.$inertia.form({
        method: 'efectivo',
        amount: this.order.total - this.order.balance,
        order_id: this.order.id,
        quick: true,
      }),
      printSize: (typeof window !== 'undefined' && localStorage.getItem('rg_thermal_size')) || '58',
      printCopies: (typeof window !== 'undefined' && parseInt(localStorage.getItem('rg_thermal_copies') || '1', 10)) || 1,
      updatingStatus: false,
      isSplitActive: false,
      splitPersons: 2,
      selectedSplitQuota: 0,
    }
  },

  computed: {
    pendingBalance() {
      return Math.max(0, parseFloat((this.currentTotal - (this.order.balance || 0)).toFixed(2)))
    },
    splitQuotaPerPerson() {
      if (!this.splitPersons || this.splitPersons < 1) return 0
      return parseFloat((this.pendingBalance / this.splitPersons).toFixed(2))
    },
    splitQuotaPerPersonBs() {
      return parseFloat((this.splitQuotaPerPerson * this.change).toFixed(2))
    },
    splitPayerList() {
      const list = []
      const quota = this.splitQuotaPerPerson
      for (let i = 1; i <= this.splitPersons; i++) {
        list.push({
          index: i,
          label: `Comensal ${i}`,
          amount: quota,
          amountBs: parseFloat((quota * this.change).toFixed(2)),
        })
      }
      return list
    },
    isRestaurantMode() {
      const acc = this.$page?.props?.auth?.user?.account || this.order?.account
      return !!(acc?.restaurant_mode || acc?.business_type === 'restaurant')
    },
    restaurantStatesList() {
      return [
        { key: 'por_iniciar', label: 'Por iniciar', dotClass: 'bg-amber-400', activeClass: 'bg-amber-500 text-white shadow-xs' },
        { key: 'en_preparacion', label: 'En preparación', dotClass: 'bg-blue-400', activeClass: 'bg-blue-600 text-white shadow-xs' },
        { key: 'pendiente_pago', label: 'Pendiente pago', dotClass: 'bg-purple-400', activeClass: 'bg-purple-600 text-white shadow-xs' },
        { key: 'finalizado', label: 'Finalizado', dotClass: 'bg-emerald-400', activeClass: 'bg-emerald-600 text-white shadow-xs' },
      ]
    },
    nextStateAction() {
      if (!this.isRestaurantMode) return null
      const state = this.order.order_state
      if (state === 'por_iniciar' || state === 'request') {
        return { key: 'en_preparacion', label: 'A Cocina', icon: 'fa-fire', btnClass: 'bg-blue-600 hover:bg-blue-700' }
      }
      if (state === 'en_preparacion') {
        return { key: 'pendiente_pago', label: 'A Mesa / Cobro', icon: 'fa-chair', btnClass: 'bg-purple-600 hover:bg-purple-700' }
      }
      if (state === 'pendiente_pago') {
        return { key: 'finalizado', label: 'Finalizar Comanda', icon: 'fa-check', btnClass: 'bg-emerald-600 hover:bg-emerald-700', markAsPaid: true }
      }
      return null
    },
    searchResults() {
      return this.products?.data || []
    },
    currentTotal() {
      if (this.isEditing) {
        let subtotal = this.editForm.products.reduce((acc, p) => acc + (p.price * p.quantity), 0)
        let discount = (subtotal * (this.editForm.discount.value || 0)) / 100
        return subtotal - discount
      }
      return this.order.total
    },
  },

  watch: {
    order: {
      deep: true,
      handler() {
        let discountPercentage = this.order.subtotal > 0 ? (this.order.discount * 100 / this.order.subtotal) : 0
        this.editForm.products = this.order.products.map(p => ({
          product_id: p.product_id,
          quantity: p.quantity,
          price: p.price,
          product: p.product,
        }))
        this.editForm.discount.value = discountPercentage
        this.editForm.client.id = this.order.client_id || (this.order.client ? this.order.client.id : '')
        this.editForm.date = this.order.date
        this.editForm.comentary = this.order.comentary || ''
        
        if (this.pendingBalance <= 0) {
          this.isSplitActive = false
          this.selectedSplitQuota = 0
        } else if (this.isSplitActive) {
          this.selectedSplitQuota = this.splitQuotaPerPerson
        }
      },
    },
  },

  methods: {
    searchProducts() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        if (this.searchTerm.length < 2) {
          return
        }
        this.$inertia.get(`/pos/${this.order.id}/show`, { search: { terms: this.searchTerm } }, {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['products'],
        })
      }, 300)
    },

    addProduct(prod) {
      if (prod.quantity <= 0) {
        return alert('No hay stock disponible de este producto')
      }
      const existing = this.editForm.products.find(p => p.product_id === prod.id)
      if (existing) {
        existing.quantity++
      } else {
        this.editForm.products.push({
          product_id: prod.id,
          quantity: 1,
          price: prod.prices && prod.prices.length > 0 ? prod.prices[0].price : 0,
          product: {
            name: prod.name,
            description: prod.description,
            code: prod.code,
          },
        })
      }
      this.searchTerm = ''
      this.$inertia.get(`/pos/${this.order.id}/show`, { search: { terms: '' } }, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        only: ['products'],
      })
    },

    saveOrder() {
      if (this.editForm.products.length === 0) {
        alert('Debe tener al menos un producto.')
        return
      }
      this.editForm.transform((data) => ({
        ...data,
        from_pos: true,
      })).put(`/orders/${this.order.id}`, {
        onSuccess: () => {
          this.isEditing = false
        },
      })
    },

    quickChangeState(newStatus, markAsPaid = false) {
      if (this.updatingStatus || this.order.order_state === newStatus) return
      this.updatingStatus = true
      this.$inertia.post(`/boards/${this.order.id}/status`, {
        status: newStatus,
        mark_as_paid: markAsPaid,
      }, {
        preserveScroll: true,
        preserveState: true,
        onFinish: () => {
          this.updatingStatus = false
        },
      })
    },

    getOrderState(state) {
      const states = {
        cancel: 'ANULADA',
        request: 'PENDIENTE POR PAGO',
        finish: 'FINALIZADA', 
        pay: 'PAGADA',
        delivery: 'EN ESPERA DE DESPACHO',
        por_iniciar: 'POR INICIAR',
        en_preparacion: 'EN PREPARACIÓN',
        pendiente_pago: 'PENDIENTE POR PAGO',
        finalizado: 'FINALIZADO',
      }
      return states[state] || state
    },

    formatPrice(value) {
      return Number(value).toFixed(2)
    },

    cancelOrder() {
      if (confirm('¿Está seguro que desea anular esta orden?')) {
        window.location.href = `/orders/${this.order.id}/cancel`
      }
    },

    deletePay(payId) {
      if (confirm('¿Está seguro que desea eliminar este pago?')) {
        this.$inertia.delete(`/orders/pay/${payId}`)
      }
    },

    setPrintSize(size) {
      this.printSize = size
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          localStorage.setItem('rg_thermal_size', size)
        } catch (e) {}
      }
    },

    changePrintCopies(delta) {
      let next = this.printCopies + delta
      if (next < 1) next = 1
      if (next > 10) next = 10
      this.printCopies = next
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          localStorage.setItem('rg_thermal_copies', next.toString())
        } catch (e) {}
      }
    },

    print(exchange) {
      const url = `/orders/${this.order.id}/report-thermical/${exchange}?size=${this.printSize}&copies=${this.printCopies}`
      window.open(
        url,
        `Venta #${this.order.number_order || this.order.id}`,
        'width=520,height=750,toolbar=no,menubar=no,scrollbars=yes',
      )
    },

    toggleSplit() {
      this.isSplitActive = !this.isSplitActive
      if (this.isSplitActive) {
        this.selectedSplitQuota = this.splitQuotaPerPerson
      } else {
        this.selectedSplitQuota = 0
      }
    },

    setSplitPersons(count) {
      const n = Math.max(2, Math.min(20, parseInt(count, 10) || 2))
      this.splitPersons = n
      if (this.isSplitActive) {
        this.selectedSplitQuota = parseFloat((this.pendingBalance / n).toFixed(2))
      }
    },

    applySplitQuotaToPayment(quota = null) {
      const val = quota !== null ? quota : this.splitQuotaPerPerson
      this.selectedSplitQuota = val
    },

    printSplitBill() {
      const url = `/orders/${this.order.id}/report-thermical/usd?size=${this.printSize}&copies=1&split=${this.splitPersons}`
      window.open(
        url,
        `Venta #${this.order.number_order || this.order.id} - ${this.splitPersons} Personas`,
        'width=520,height=750,toolbar=no,menubar=no,scrollbars=yes',
      )
    },
  },
}
</script>
